// Alfabeto base62 sin caracteres ambiguos (evita 0/O y l/1/I)
export const BASE62_ALPHABET =
  "23456789ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz";

// Mapa de claves por kid
export interface KeyMap {
  [kid: string]: string; // kid -> salt
}

// Configuración por entorno con rotación de claves
export interface OpaqueIdConfig {
  version: string; // 1 carácter de versión
  minLength: number; // Longitud mínima fija (12 con kid)
  alphabet: string; // Alfabeto base62
  keyMap: KeyMap; // Mapa de kids a salts
  activeKid: string; // Kid activo para encode
  checksumMod: number; // Módulo para checksum
}

// Códigos de error tipados
export enum OpaqueIdError {
  INVALID_VERSION = "INVALID_VERSION",
  INVALID_KID = "INVALID_KID",
  INVALID_LENGTH = "INVALID_LENGTH",
  INVALID_CHAR = "INVALID_CHAR",
  CHECKSUM_MISMATCH = "CHECKSUM_MISMATCH",
  DECODE_FAILED = "DECODE_FAILED",
}

export class OpaqueIdException extends Error {
  constructor(
    public readonly code: OpaqueIdError,
    message: string,
    public readonly publicId?: string
  ) {
    super(message);
    this.name = "OpaqueIdException";
  }
}

/**
 * Convierte un número a base62 usando el alfabeto especificado
 */
function toBase62(num: number, alphabet: string): string {
  if (num === 0) return alphabet[0];

  let result = "";
  const base = alphabet.length;

  while (num > 0) {
    result = alphabet[num % base] + result;
    num = Math.floor(num / base);
  }

  return result;
}

/**
 * Convierte una cadena base62 a número usando el alfabeto especificado
 */
function fromBase62(str: string, alphabet: string): number {
  let result = 0;
  const base = alphabet.length;

  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const index = alphabet.indexOf(char);

    if (index === -1) {
      throw new OpaqueIdException(
        OpaqueIdError.INVALID_CHAR,
        `Invalid character in base62 string: ${char}`
      );
    }

    result = result * base + index;
  }

  return result;
}

/**
 * Convierte un array de bytes a base62
 * Trata el array como un número grande en little-endian
 */
function bytesToBase62(bytes: number[], alphabet: string): string {
  if (bytes.length === 0) return alphabet[0];

  // Convertir bytes a un "número grande" usando base 256
  let result = "";
  const base = alphabet.length;
  
  // Crear copia para no mutar el original
  const work = [...bytes];
  
  // Algoritmo de conversión de base (256 → 62)
  while (work.some(b => b !== 0)) {
    let remainder = 0;
    
    // Dividir todo el array entre 'base' (62)
    for (let i = work.length - 1; i >= 0; i--) {
      const current = remainder * 256 + work[i];
      work[i] = Math.floor(current / base);
      remainder = current % base;
    }
    
    result = alphabet[remainder] + result;
  }
  
  return result || alphabet[0];
}

/**
 * Convierte un string base62 a array de bytes
 * Reversa de bytesToBase62
 */
function base62ToBytes(str: string, alphabet: string): number[] {
  if (!str || str === alphabet[0]) return [0];

  const base = alphabet.length;
  const result: number[] = [];
  
  // Convertir cada carácter base62 a su valor
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    const value = alphabet.indexOf(char);
    
    if (value === -1) {
      throw new OpaqueIdException(
        OpaqueIdError.INVALID_CHAR,
        `Invalid base62 character: ${char}`
      );
    }
    
    // Multiplicar resultado actual por base y sumar nuevo valor
    let carry = value;
    for (let j = 0; j < result.length; j++) {
      const current = result[j] * base + carry;
      result[j] = current % 256;
      carry = Math.floor(current / 256);
    }
    
    while (carry > 0) {
      result.push(carry % 256);
      carry = Math.floor(carry / 256);
    }
  }
  
  return result.length > 0 ? result : [0];
}
/**
 * Calcula checksum usando módulo primo
 */
function calculateChecksum(
  versionKidAndBody: string,
  salt: string,
  config: OpaqueIdConfig
): string {
  let sum = 0;

  // Incluir salt en el cálculo
  const input = salt + versionKidAndBody;

  for (let i = 0; i < input.length; i++) {
    sum += input.charCodeAt(i) * (i + 1);
  }

  const checksumIndex = sum % config.checksumMod;

  // Asegurar que el índice esté dentro del rango del alfabeto
  const safeIndex = checksumIndex % config.alphabet.length;
  return config.alphabet[safeIndex];
}

/**
 * Aplica transformación simple basada en salt para ofuscación
 */
function applySaltTransform(num: number, salt: string): number {
  let saltHash = 0;
  for (let i = 0; i < salt.length; i++) {
    saltHash = ((saltHash << 5) - saltHash + salt.charCodeAt(i)) & 0xffffffff;
  }

  // XOR simple para ofuscación (no es cifrado seguro)
  return num ^ Math.abs(saltHash);
}

/**
 * Revierte la transformación de salt
 */
function reverseSaltTransform(transformedNum: number, salt: string): number {
  // XOR es su propia inversa
  return applySaltTransform(transformedNum, salt);
}

/**
 * Codifica un entero a identificador opaco
 * @param num - Entero a codificar (debe ser >= 0)
 * @param config - Configuración del esquema
 * @returns Identificador opaco con formato <versión><cuerpo><checksum>
 */
export function encode(num: number, config: OpaqueIdConfig): string {
  if (num < 0 || !Number.isInteger(num)) {
    throw new OpaqueIdException(
      OpaqueIdError.DECODE_FAILED,
      "Input must be a non-negative integer"
    );
  }

  // Obtener salt del kid activo
  const salt = config.keyMap[config.activeKid];
  if (!salt) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_KID,
      `Active kid '${config.activeKid}' not found in keyMap`
    );
  }

  // Aplicar transformación de salt
  const transformed = applySaltTransform(num, salt);

  // Convertir a base62
  let body = toBase62(transformed, config.alphabet);

  // Asegurar longitud mínima (sin contar versión, kid y checksum)
  const targetBodyLength = config.minLength - 3; // -1 versión, -1 kid, -1 checksum
  while (body.length < targetBodyLength) {
    body = config.alphabet[0] + body;
  }

  // Construir versión + kid + cuerpo
  const versionKidAndBody = config.version + config.activeKid + body;

  // Calcular checksum
  const checksum = calculateChecksum(versionKidAndBody, salt, config);

  return versionKidAndBody + checksum;
}

/**
 * Decodifica un identificador opaco a entero
 * @param publicId - Identificador opaco a decodificar
 * @param config - Configuración del esquema
 * @returns Entero original
 */
export function decode(publicId: string, config: OpaqueIdConfig): number {
  // Validar longitud mínima
  if (publicId.length < config.minLength) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_LENGTH,
      `ID must be at least ${config.minLength} characters`,
      publicId
    );
  }

  // Extraer componentes
  const version = publicId[0];
  const kid = publicId[1];
  const checksum = publicId[publicId.length - 1];
  const body = publicId.slice(2, -1);

  // Validar versión
  if (version !== config.version) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_VERSION,
      `Invalid version: expected ${config.version}, got ${version}`,
      publicId
    );
  }

  // Validar kid y obtener salt
  const salt = config.keyMap[kid];
  if (!salt) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_KID,
      `Invalid kid: ${kid}`,
      publicId
    );
  }

  // Validar caracteres del alfabeto
  const allChars = version + body + checksum;
  for (const char of allChars) {
    if (!config.alphabet.includes(char)) {
      throw new OpaqueIdException(
        OpaqueIdError.INVALID_CHAR,
        `Invalid character: ${char}`,
        publicId
      );
    }
  }

  // Verificar checksum
  const versionKidAndBody = version + kid + body;
  const expectedChecksum = calculateChecksum(versionKidAndBody, salt, config);

  if (checksum !== expectedChecksum) {
    throw new OpaqueIdException(
      OpaqueIdError.CHECKSUM_MISMATCH,
      "Checksum validation failed",
      publicId
    );
  }

  try {
    // Convertir cuerpo de base62 a número
    const transformed = fromBase62(body, config.alphabet);

    // Revertir transformación de salt
    const original = reverseSaltTransform(transformed, salt);

    return original;
  } catch (error) {
    if (error instanceof OpaqueIdException) {
      throw error;
    }

    throw new OpaqueIdException(
      OpaqueIdError.DECODE_FAILED,
      "Failed to decode ID",
      publicId
    );
  }
}

/**
 * Codifica un array de bytes a identificador opaco
 * Versión extendida de encode() para datos de múltiples bytes
 */
export function encodeBytes(bytes: number[], config: OpaqueIdConfig): string {
  if (bytes.length === 0) {
    throw new OpaqueIdException(
      OpaqueIdError.DECODE_FAILED,
      "Input bytes array cannot be empty"
    );
  }

  // Obtener salt del kid activo
  const salt = config.keyMap[config.activeKid];
  if (!salt) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_KID,
      `Active kid '${config.activeKid}' not found in keyMap`
    );
  }

  // Aplicar transformación de salt a cada byte
  const transformedBytes = bytes.map(byte => 
    applySaltTransform(byte, salt) & 0xFF // Mantener en rango 0-255
  );

  // Convertir bytes transformados a base62
  let body = bytesToBase62(transformedBytes, config.alphabet);

  // Asegurar longitud mínima (sin contar versión, kid y checksum)
  const targetBodyLength = config.minLength - 3;
  while (body.length < targetBodyLength) {
    body = config.alphabet[0] + body;
  }

  // Construir versión + kid + cuerpo
  const versionKidAndBody = config.version + config.activeKid + body;

  // Calcular checksum
  const checksum = calculateChecksum(versionKidAndBody, salt, config);

  return versionKidAndBody + checksum;
}

/**
 * Decodifica un identificador opaco a array de bytes
 * Versión extendida de decode() para datos de múltiples bytes
 */
export function decodeBytes(publicId: string, config: OpaqueIdConfig): number[] {
  // Validar longitud mínima
  if (publicId.length < config.minLength) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_LENGTH,
      `ID must be at least ${config.minLength} characters`,
      publicId
    );
  }

  // Extraer componentes (igual que decode original)
  const version = publicId[0];
  const kid = publicId[1];
  const checksum = publicId[publicId.length - 1];
  const body = publicId.slice(2, -1);

  // Validar versión
  if (version !== config.version) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_VERSION,
      `Invalid version: expected ${config.version}, got ${version}`,
      publicId
    );
  }

  // Validar kid y obtener salt
  const salt = config.keyMap[kid];
  if (!salt) {
    throw new OpaqueIdException(
      OpaqueIdError.INVALID_KID,
      `Invalid kid: ${kid}`,
      publicId
    );
  }

  // Validar caracteres del alfabeto
  const allChars = version + body + checksum;
  for (const char of allChars) {
    if (!config.alphabet.includes(char)) {
      throw new OpaqueIdException(
        OpaqueIdError.INVALID_CHAR,
        `Invalid character: ${char}`,
        publicId
      );
    }
  }

  // Verificar checksum
  const versionKidAndBody = version + kid + body;
  const expectedChecksum = calculateChecksum(versionKidAndBody, salt, config);

  if (checksum !== expectedChecksum) {
    throw new OpaqueIdException(
      OpaqueIdError.CHECKSUM_MISMATCH,
      "Checksum validation failed",
      publicId
    );
  }

  try {
    // Convertir cuerpo de base62 a bytes
    const transformedBytes = base62ToBytes(body, config.alphabet);

    // Revertir transformación de salt en cada byte
    const originalBytes = transformedBytes.map(byte =>
      reverseSaltTransform(byte, salt) & 0xFF
    );

    return originalBytes;
  } catch (error) {
    if (error instanceof OpaqueIdException) {
      throw error;
    }

    throw new OpaqueIdException(
      OpaqueIdError.DECODE_FAILED,
      "Failed to decode ID to bytes",
      publicId
    );
  }
}

/**
 * Validación ligera sin decode completo
 * @param publicId - Identificador a validar
 * @param config - Configuración del esquema
 * @returns true si pasa validaciones básicas
 */
export function validateLight(
  publicId: string,
  config: OpaqueIdConfig
): boolean {
  // Validar longitud mínima
  if (publicId.length < config.minLength) {
    return false;
  }

  // Validar versión
  if (publicId[0] !== config.version) {
    return false;
  }

  // Validar kid existe
  const kid = publicId[1];
  if (!config.keyMap[kid]) {
    return false;
  }

  // Validar caracteres del alfabeto (excluyendo el kid, que puede no estar en el alfabeto)
  const charsToValidate = publicId[0] + publicId.slice(2);
  for (const char of charsToValidate) {
    if (!config.alphabet.includes(char)) {
      return false;
    }
  }

  // Validar checksum básico
  const salt = config.keyMap[kid];
  const versionKidAndBody = publicId.slice(0, -1);
  const checksum = publicId[publicId.length - 1];
  const expectedChecksum = calculateChecksum(versionKidAndBody, salt, config);

  return checksum === expectedChecksum;
}

/**
 * Valida un identificador opaco con decode completo
 * @param publicId - Identificador a validar
 * @param config - Configuración del esquema
 * @returns true si es válido, false en caso contrario
 */
export function validate(publicId: string, config: OpaqueIdConfig): boolean {
  try {
    decode(publicId, config);
    return true;
  } catch {
    return false;
  }
}

/**
 * Verifica que decode(encode(n)) === n para un rango de valores
 * Útil para testing y validación de configuración
 */
export function verifyIdempotence(
  config: OpaqueIdConfig,
  testValues: number[] = [0, 1, 42, 1000, 999999]
): boolean {
  try {
    for (const num of testValues) {
      const encoded = encode(num, config);
      const decoded = decode(encoded, config);

      if (decoded !== num) {
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Convierte un string a array de bytes (números 0-255)
 * @example stringToBytes("Hi") → [72, 105]
 */
export function stringToBytes(str: string): number[] {
  const encoder = new TextEncoder();
  return Array.from(encoder.encode(str));
}

/**
 * Convierte array de bytes a string
 * @example bytesToString([72, 105]) → "Hi"
 */
export function bytesToString(bytes: number[]): string {
  const decoder = new TextDecoder();
  return decoder.decode(new Uint8Array(bytes));
}